import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ResponsiveContainer,
  ResponsiveContent,
  ResponsiveHeader,
  ResponsiveFooter,
  Card,
  Button,
} from '../ui';
import { useSessionPersistence } from '../../hooks/useSessionPersistence';
import { FinishFlowAction } from '../../flows/FinishFlowAction';
import { DocumentsIcon } from '../ui/Icons';
import { documentService } from '../../services/documentService';
import type { AppDocument } from '../../types/document';

interface LegalDocument {
  id: string;
  type: 'will' | 'trust' | 'healthcare_power_of_attorney' | 'healthcare_directive' | 'other';
  name: string;
  description?: string;
  uploadDate: string;
  lastModified?: string;
  notes?: string;
  documents: AppDocument[];
}

const DOCUMENT_TYPES = {
  will: {
    label: 'Last Will and Testament',
    icon: '📄',
    description: 'Your most recent will detailing asset distribution',
  },
  trust: {
    label: 'Living Trust',
    icon: '🏛️',
    description:
      'Legal documents that specify how your assets are held, managed, and distributed by your chosen trustee.',
  },
  healthcare_power_of_attorney: {
    label: 'Healthcare Power of Attorney',
    icon: '✍️',
    description: 'Legal authority for healthcare decisions',
  },
  healthcare_directive: {
    label: 'Healthcare Directive',
    icon: '🏥',
    description: 'Your healthcare wishes and end-of-life directives',
  },
  other: {
    label: 'Other Legal Documents',
    icon: '📎',
    description: 'Additional legal documents',
  },
} as const;

interface Executor {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

const normalizeDocuments = (docs: unknown): AppDocument[] => {
  return documentService.normalizeMany(docs);
};

const Frame215: React.FC = () => {
  const navigate = useNavigate();
  const { session, updateTaskProgress } = useSessionPersistence();

  const [documents, setDocuments] = useState<LegalDocument[]>([]);
  const [executors, setExecutors] = useState<Executor[]>([]);

  useEffect(() => {
    const savedDocs = session.taskProgress?.legal_documents?.answers?.documents ?? [];
    setDocuments(
      savedDocs.map((doc: LegalDocument) => ({
        ...doc,
        documents: normalizeDocuments(doc.documents || []),
      }))
    );

    const fromExecutorsTask =
      session.taskProgress?.executors?.answers?.selectedExecutors ?? null;

    const fromLegacyLegalEssentials =
      session.taskProgress?.legal_essentials?.answers?.executors ?? [];

    const resolvedExecutors = Array.isArray(fromExecutorsTask)
      ? fromExecutorsTask
      : fromLegacyLegalEssentials;

    setExecutors(resolvedExecutors);
  }, [
    session.taskProgress?.legal_documents?.answers?.documents,
    session.taskProgress?.executors?.answers?.selectedExecutors,
    session.taskProgress?.legal_essentials?.answers?.executors,
  ]);

  const documentsByType = useMemo(() => {
    return documents.reduce((acc, doc) => {
      const type = doc.type;
      if (!acc[type]) acc[type] = [];
      acc[type].push(doc);
      return acc;
    }, {} as Record<LegalDocument['type'], LegalDocument[]>);
  }, [documents]);

  const openDocument = (doc: AppDocument) => {
    if (!doc.id) return;
    documentService.open(doc.id);
  };

  const downloadDocument = async (doc: AppDocument) => {
    if (!doc.id) return;

    try {
      await documentService.download(doc.id, doc.name);
    } catch (error) {
      console.error('[Frame215] Failed to download document:', error);
      alert('Failed to download document.');
    }
  };

  const handleEditExecutors = () => {
    navigate('/frame-261');
  };

  const handleEditDocuments = () => {
    navigate('/frame-214');
  };

  const handleRemoveDocument = async (docId: string) => {
    const docToDelete = documents.find((doc) => doc.id === docId);
    const attachedDocs = docToDelete?.documents || [];

    for (const doc of attachedDocs) {
      try {
        await documentService.delete(doc.id);
      } catch (error) {
        console.error('[Frame215] Failed to delete document while deleting legal document:', error);
      }
    }

    const updatedDocuments = documents.filter((doc) => doc.id !== docId);
    setDocuments(updatedDocuments);
    await updateTaskProgress('legal_documents', { documents: updatedDocuments });
  };

  const handleRemoveAttachedDocument = async (legalDocId: string, documentIndex: number) => {
    const legalDoc = documents.find((doc) => doc.id === legalDocId);
    const docToRemove = legalDoc?.documents?.[documentIndex];
    if (!docToRemove) return;

    try {
      await documentService.delete(docToRemove.id);
    } catch (error) {
      console.error('[Frame215] Failed to delete saved document:', error);
      alert('Failed to delete document.');
      return;
    }

    const updatedDocuments = documents.map((doc) => {
      if (doc.id !== legalDocId) return doc;
      return {
        ...doc,
        documents: normalizeDocuments(doc.documents || []).filter((_, index) => index !== documentIndex),
      };
    });

    setDocuments(updatedDocuments);
    await updateTaskProgress('legal_documents', { documents: updatedDocuments });
  };

  return (
    <ResponsiveContainer>
      <ResponsiveContent>
        <ResponsiveHeader title="Legal and Executor Summary" />

        <div className="space-y-6">
          <Card>
            <h3 className="text-lg font-semibold text-stone-800 mb-4">Documents Overview</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-cyan-900">{documents.length}</div>
                <div className="text-sm text-stone-600">Total Documents</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  {Object.keys(documentsByType).length}
                </div>
                <div className="text-sm text-stone-600">Categories</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">
                  {documents.filter((d) => d.notes).length}
                </div>
                <div className="text-sm text-stone-600">With Notes</div>
              </div>
            </div>
          </Card>

          <Card>
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-lg font-semibold text-stone-800">
                Executor{executors.length > 1 ? 's' : ''}
              </h3>
              <Button variant="outline" size="sm" onClick={handleEditExecutors}>
                Edit
              </Button>
            </div>

            {executors.length > 0 ? (
              <div className="space-y-3">
                {executors.map((executor) => (
                  <div key={executor.id} className="bg-stone-50 rounded-lg p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <h4 className="font-medium text-stone-800">
                          {executor.firstName} {executor.lastName}
                        </h4>
                        <p className="text-sm text-stone-600 mt-1">{executor.email}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-stone-500">
                <p>No executors selected yet.</p>
                <Button variant="outline" onClick={handleEditExecutors} className="mt-2">
                  Select Executor
                </Button>
              </div>
            )}
          </Card>

          <Card>
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-lg font-semibold text-stone-800">Legal Documents</h3>
              <Button variant="outline" size="sm" onClick={handleEditDocuments}>
                Edit
              </Button>
            </div>

            <div className="max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
              {documents.length > 0 ? (
                <div className="space-y-8">
                  {Object.entries(documentsByType).map(([type, docs]) => (
                    <div key={type}>
                      <h4 className="text-sm font-bold text-stone-400 uppercase tracking-widest mb-3 border-b border-stone-100 pb-1">
                        {DOCUMENT_TYPES[type as keyof typeof DOCUMENT_TYPES].label} ({docs.length})
                      </h4>

                      <div className="pl-7 space-y-2">
                        {docs.map((doc) => {
                          const attachedDocs = normalizeDocuments(doc.documents || []);

                          return (
                            <div key={doc.id} className="bg-stone-50 rounded-lg p-3">
                              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-medium text-stone-800 mb-2 truncate" title={doc.name}>
                                    {doc.name}
                                  </div>

                                  {attachedDocs.length > 0 && (
                                    <div className="space-y-2">
                                      {attachedDocs.map((attachedDoc, index) => (
                                        <div
                                          key={`${doc.id}-${attachedDoc.id}-${index}`}
                                          className="flex flex-col gap-3 rounded-md border border-stone-200 bg-white px-3 py-3 sm:flex-row sm:items-center sm:justify-between"
                                        >
                                          <button
                                            type="button"
                                            onClick={() => openDocument(attachedDoc)}
                                            className="flex items-start gap-2 min-w-0 w-full text-left sm:flex-1"
                                          >
                                            <DocumentsIcon className="w-4 h-4 text-cyan-700 flex-shrink-0 mt-0.5" />
                                            <span
                                              className="text-sm font-medium text-stone-800 break-words whitespace-normal"
                                              title={attachedDoc.name}
                                            >
                                              {attachedDoc.name}
                                            </span>
                                          </button>

                                          <div className="flex flex-wrap items-center gap-x-3 gap-y-2 sm:shrink-0">
                                            <button
                                              type="button"
                                              onClick={() => downloadDocument(attachedDoc)}
                                              className="text-cyan-600 hover:text-cyan-800 text-xs font-medium"
                                              title="Download document"
                                            >
                                              Download
                                            </button>

                                            <button
                                              type="button"
                                              onClick={() => handleRemoveAttachedDocument(doc.id, index)}
                                              className="text-red-500 hover:text-red-700 text-xs shrink-0"
                                              title="Remove document"
                                            >
                                              Remove
                                            </button>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )}

                                  {doc.notes && (
                                    <p className="text-sm text-stone-600 mt-2 break-words">{doc.notes}</p>
                                  )}

                                  <div className="text-xs text-stone-500 mt-2">
                                    Uploaded {new Date(doc.uploadDate).toLocaleDateString()}
                                  </div>
                                </div>

                                <button
                                  type="button"
                                  onClick={() => handleRemoveDocument(doc.id)}
                                  className="text-red-500 hover:text-red-700 text-xs shrink-0"
                                  title="Remove document entry"
                                >
                                  Remove
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-stone-500">
                  <p>No documents uploaded yet.</p>
                  <Button variant="outline" onClick={handleEditDocuments} className="mt-2">
                    Upload Documents
                  </Button>
                </div>
              )}
            </div>
          </Card>

          {documents.length === 0 && (
            <Card className="bg-yellow-50 border-yellow-100">
              <div className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full bg-yellow-400 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-medium text-yellow-900">Important Documents Missing</h4>
                  <p className="text-sm text-yellow-800 mt-1">
                    You haven&apos;t uploaded any legal documents yet. Having these documents readily
                    available ensures your wishes are known and can be carried out. Consider working with
                    a legal professional to create or update any missing documents.
                  </p>
                </div>
              </div>
            </Card>
          )}
        </div>
      </ResponsiveContent>

      <ResponsiveFooter
        title="Complete Your Legal Essentials"
        subtitle="Review your legal documents and executor"
        progressPercent={100}
        onBack={handleEditExecutors}
        backText="Back"
        nextNode={
          <FinishFlowAction
            flowId="legal_essentials"
            onFinished={() => navigate('/frame-140')}
            buttonText="Continue to Dashboard"
            className="!bg-cyan-700 hover:!bg-cyan-800 rounded-sm"
          />
        }
      />
    </ResponsiveContainer>
  );
};

export default Frame215;